import DOMPurify from "dompurify";

/**
 * Ported from the admin app's resources/js/lib/sanitizeHtml.js so creator-
 * authored rich text is filtered by the SAME allowlist on both sides. The admin
 * sanitizes its own previews; before this, the learner app — the side that
 * actually serves this HTML to members — rendered it raw.
 *
 * Keep the two lists in sync. If a tag is added here but not there (or the
 * reverse), a creator sees one thing in the editor preview and members get
 * another.
 */

// Force any target="_blank" link to carry rel="noopener noreferrer" so
// sanitized rich text can't be used for reverse-tabnabbing.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// A creator can embed a whole mini page in a lesson as <iframe srcdoc="…">
// (calculators, styled guides). It runs in a sandbox WITHOUT allow-same-origin,
// so its scripts get an opaque origin and can't read the member's session or
// the page around it. Whatever sandbox the creator wrote is replaced, and a
// src must be https. allow-downloads lets a calculator save the PDF it builds.
const IFRAME_SANDBOX = "allow-scripts allow-forms allow-popups allow-modals allow-downloads";
const heldSrcdoc = new WeakMap();
const isForeign = (src) => {
  try {
    return !!src && new URL(src).host !== window.location.host;
  } catch {
    return false;
  }
};
DOMPurify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName !== "iframe") return;
  const src = node.getAttribute("src");
  if (src && !/^https:\/\//i.test(src.trim())) node.removeAttribute("src");
  // srcdoc is a whole HTML page, so it always holds "</style>" or "</title>",
  // which DOMPurify's attribute check drops. Hold it aside and put it back
  // once the attributes are done: the sandbox above contains it, and the
  // value is re-escaped when the output is serialized.
  if (node.hasAttribute("srcdoc")) {
    heldSrcdoc.set(node, node.getAttribute("srcdoc"));
    node.removeAttribute("srcdoc");
  }
});
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "IFRAME") return;
  const srcdoc = heldSrcdoc.get(node);
  // A player from another site (YouTube, Vimeo) needs its own cookies and
  // storage; with a foreign origin that grants nothing on ours. A srcdoc frame
  // would inherit OUR origin, so it never gets this.
  node.setAttribute("sandbox", srcdoc === undefined && isForeign(node.getAttribute("src"))
    ? `${IFRAME_SANDBOX} allow-same-origin allow-presentation`
    : IFRAME_SANDBOX);
  if (srcdoc !== undefined) node.setAttribute("srcdoc", srcdoc);
});

// Allowlist for creator-authored rich text (lesson detailed descriptions, goal
// descriptions, privacy/terms policy copy). DOMPurify strips scripts, event
// handlers, and unsafe URL schemes (javascript:, data:) by default.
//
// `style` is allowed because the Jodit editor stores formatting inline —
// including the ul/ol marker choice as `list-style-type`, which the
// `.rich-content` rules in index.css deliberately defer to.
const RICH_TEXT = {
  ALLOWED_TAGS: [
    "p", "br", "hr", "span", "div", "b", "strong", "i", "em", "u", "s", "strike",
    "sub", "sup", "mark", "small",
    "ul", "ol", "li", "a", "blockquote", "pre", "code",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "img", "figure", "figcaption",
    // colgroup/col matter more than they look: a table pasted from Google
    // Sheets carries `table-layout: fixed; width: 0px` and keeps its only
    // column width in <col width="…">. Dropping those two tags left a fixed
    // table with no columns, which lays out ~4px wide and is then clipped by
    // the td's own `overflow: hidden` — the lesson rendered as a tall empty
    // box. See the .rich-content table rules for the second half of this.
    "table", "caption", "colgroup", "col", "thead", "tbody", "tfoot", "tr", "td", "th",
    "iframe",
  ],
  ALLOWED_ATTR: [
    "href", "target", "rel", "src", "alt", "title",
    "width", "height", "style", "class", "colspan", "rowspan", "align",
    "span", // <col span="2">
    // <iframe>; sandbox is forced by the hook above
    "allowfullscreen", "frameborder", "loading", "sandbox",
  ],
  ALLOW_DATA_ATTR: false,
};

/**
 * Sanitize an HTML string against the rich-text allowlist.
 * @param {*} html
 * @param {object} [options] override DOMPurify config for special cases.
 * @returns {string}
 */
export function sanitizeHtml(html, options) {
  if (html === null || html === undefined) return "";
  return DOMPurify.sanitize(String(html), options || RICH_TEXT);
}

/**
 * Convenience for React's `dangerouslySetInnerHTML` prop:
 *   <div dangerouslySetInnerHTML={safeHtml(userHtml)} />
 * @param {*} html
 * @param {object} [options]
 * @returns {{ __html: string }}
 */
export function safeHtml(html, options) {
  return { __html: sanitizeHtml(html, options) };
}
